// crewbit-admin/src/pages/Money/Outstanding.tsx
//
// SPEC: docs/references/MONEY_MODULE_SPEC_v1.md §Outstanding.7.2.
// Top-level page for /money/outstanding. Composes PageHeader +
// AgingHeroStrip + OutstandingTable. Read-only with Remind side-effect.
//
// Refresh policy is OWNED by useOutstandingInvoices and useFinancialSummary
// (Page Visibility 30s focus throttle + window listeners on
// MONEY_MUTATION_EVENT and ACTIVE_BUSINESS_CHANGED_EVENT). This page MUST
// NOT add visibility/window listeners of its own — would double-fire
// (mirrors Overview.tsx §10.10 lock).

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, RefreshCw, X } from 'lucide-react';
import { PageHeader } from '../../components/common/PageHeader';
import { Toast } from '../../components/common/Toast';
import { EmptyState } from '../../components/common/EmptyState';
import { Skeleton, TableSkeleton } from '../../components/common/Skeleton';
import { AgingHeroStrip } from '../../components/money/AgingHeroStrip';
import { OutstandingTable } from '../../components/money/OutstandingTable';
import { MoneyInvoiceDetailPanel } from '../../components/money/MoneyInvoiceDetailPanel';
import SettingsGearLink from '../../components/money/settings/SettingsGearLink';
import { useFinancialSummary } from '../../hooks/useFinancialSummary';
import {
  useOutstandingInvoices,
  type OutstandingInvoiceRow,
} from '../../hooks/useOutstandingInvoices';
import { type AgingBucket } from '../../lib/aging';
import { sendReminder } from '../../lib/invoiceMutations';
import { lookupErrorCopy } from '../../lib/errorMap';
import { admin, palette, radii, shadow, spacing, typography } from '../../styles/tokens';
import type { Invoice } from '../../types';

type ToastState = {
  msg: string;
  type: 'success' | 'error' | 'warning' | 'info';
} | null;

const pageStyle: React.CSSProperties = {
  padding: spacing.xl,
  background: admin.bg,
  minHeight: '100vh',
};

// C3 — aging-bucket deeplink/filter (clears §Money.OutstandingBucketDeeplink).
const VALID_BUCKETS: AgingBucket[] = ['current', '1_30', '31_60', '61_plus'];
const BUCKET_LABEL: Record<AgingBucket, string> = {
  current: 'Current',
  '1_30': '1–30 days',
  '31_60': '31–60 days',
  '61_plus': '61+ days',
};

export function MoneyOutstandingAdmin() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const summary = useFinancialSummary({ period: '30d' });
  const list = useOutstandingInvoices();
  const [toast, setToast] = useState<ToastState>(null);
  const invoiceIdParam = searchParams.get('invoice_id');

  // ?bucket= aging filter. Unknown/empty → null (show all, no crash).
  const bucketParam = searchParams.get('bucket');
  const activeBucket: AgingBucket | null =
    bucketParam && (VALID_BUCKETS as readonly string[]).includes(bucketParam)
      ? (bucketParam as AgingBucket)
      : null;

  // Merge-preserving param writes so the ?invoice_id slide-over and the ?bucket
  // filter stay independent (neither clobbers the other).
  const setBucket = (b: AgingBucket | null) => {
    const next = new URLSearchParams(searchParams);
    if (b === null) next.delete('bucket');
    else next.set('bucket', b);
    setSearchParams(next);
  };

  // Session 127 §3d — Edit-from-detail-panel handler. Matrix only emits 'edit'
  // for drafts; route to /money/invoices with the resume hint. Navigation (not
  // setSearchParams) because Outstanding does not mount the authoring panel —
  // MoneyInvoicesPage owns it. Operator returns to Invoices on close.
  const handleEditAction = (invoice: Invoice) => {
    if (invoice.status === 'draft') {
      void navigate(`/money/invoices?action=edit-draft&invoice_id=${invoice.id}`);
    }
  };

  // Slide-over mounts at the page level so direct-URL deep links
  // (?invoice_id=...) open the panel even on loading / empty branches.
  const detailPanel = (
    <MoneyInvoiceDetailPanel
      invoiceId={invoiceIdParam}
      onClose={() => {
        const next = new URLSearchParams(searchParams);
        next.delete('invoice_id');
        setSearchParams(next);
      }}
      onEditAction={handleEditAction}
    />
  );

  useEffect(() => {
    document.title = 'CrewBit · Outstanding';
  }, []);

  // Branch 1 — RLS / no-business gate. Outer <RequiresActiveBusinessGate>
  // already short-circuits at the route boundary; defense-in-depth.
  if (list.requiresBusinessSelection || summary.requiresBusinessSelection) {
    return (
      <div style={pageStyle}>
        <PageHeader title="Outstanding" back={{ to: '/money', label: 'Money' }} secondaryActions={<SettingsGearLink />} />
        <EmptyState
          title="Pick a business"
          subtitle="Switch to an active business from the sidebar to see outstanding invoices."
        />
        {detailPanel}
      </div>
    );
  }

  // Branch 2 — initial load. Stale-while-revalidate kicks in once data exists.
  if ((list.isLoading && !list.data) || (summary.isLoading && !summary.data)) {
    return (
      <div style={pageStyle}>
        <PageHeader title="Outstanding" back={{ to: '/money', label: 'Money' }} secondaryActions={<SettingsGearLink />} />
        <div style={{ display: 'flex', gap: spacing.md, marginBottom: spacing.lg }}>
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              style={{
                flex: 1,
                minWidth: 0,
                padding: '16px 18px',
                background: palette.white,
                border: `1px solid ${admin.border}`,
                borderRadius: admin.cardRadius,
              }}
            >
              <Skeleton width={80} height={10} />
              <div style={{ height: 12 }} />
              <Skeleton width={120} height={26} />
              <div style={{ height: 8 }} />
              <Skeleton width={60} height={10} />
            </div>
          ))}
        </div>
        <div
          style={{
            background: palette.white,
            borderRadius: admin.cardRadius,
            border: `1px solid ${admin.border}`,
            padding: spacing.md,
            boxShadow: shadow.card,
          }}
        >
          <TableSkeleton rows={5} columns={7} />
        </div>
        {detailPanel}
      </div>
    );
  }

  // Branch 3 — first-load error (data still null on either hook).
  if ((list.error && !list.data) || (summary.error && !summary.data)) {
    return (
      <div style={pageStyle}>
        <PageHeader title="Outstanding" back={{ to: '/money', label: 'Money' }} secondaryActions={<SettingsGearLink />} />
        <div
          style={{
            background: palette.amberWash,
            border: `1px solid ${palette.amber}`,
            borderRadius: admin.cardRadius,
            padding: spacing.lg,
            display: 'flex',
            alignItems: 'center',
            gap: spacing.md,
            boxShadow: shadow.card,
          }}
        >
          <AlertCircle size={20} color={palette.amber} strokeWidth={2} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: palette.amber }}>
              We couldn't load your outstanding invoices.
            </div>
            <div style={{ fontSize: 13, color: palette.amber, marginTop: 4 }}>
              Check your connection and try again.
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              void list.refetch();
              void summary.refetch();
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 14px',
              borderRadius: radii.sm,
              border: 'none',
              background: palette.amber,
              color: palette.white,
              fontSize: 13,
              fontWeight: 600,
              fontFamily: 'inherit',
              cursor: 'pointer',
            }}
          >
            <RefreshCw size={13} />
            Retry
          </button>
        </div>
        {detailPanel}
      </div>
    );
  }

  const rows = list.data ?? [];
  const summaryData = summary.data;
  if (!summaryData) return null;

  // Branch 4 — empty (calm-empty).
  if (rows.length === 0) {
    return (
      <div style={pageStyle}>
        <PageHeader title="Outstanding" back={{ to: '/money', label: 'Money' }} description="All caught up." secondaryActions={<SettingsGearLink />} />
        <EmptyState
          title="All clear"
          subtitle="Nobody owes you anything right now."
        />
        {detailPanel}
      </div>
    );
  }

  // Branch 5 — happy path.
  // Session 125: open the §InvoiceDetail slide-over via URL search-param
  // (?invoice_id=...) instead of navigating to a legacy /financials/{id}
  // route, which never existed and silently fell through to /today.
  const handleTap = (row: OutstandingInvoiceRow) => {
    const next = new URLSearchParams(searchParams);
    next.set('invoice_id', row.id);
    setSearchParams(next);
  };

  const handleRemind = async (row: OutstandingInvoiceRow) => {
    const result = await sendReminder({
      invoiceId: row.id,
      idempotencyKey: crypto.randomUUID(),
    });
    if (result.success) {
      setToast({ msg: 'Reminder queued', type: 'success' });
    } else {
      setToast({
        msg: lookupErrorCopy('send_reminder', result.error.code),
        type: 'error',
      });
    }
  };

  const count = summaryData.outstanding.invoice_count;
  const oldest = summaryData.outstanding.oldest_days;
  const description = `${count} open invoice${count === 1 ? '' : 's'} · oldest ${oldest}d outstanding`;

  // Client-side aging filter over the already-fetched rows (no new query).
  const visibleRows = activeBucket ? rows.filter((r) => r.bucket === activeBucket) : rows;

  return (
    <div style={pageStyle}>
      <PageHeader title="Outstanding" back={{ to: '/money', label: 'Money' }} description={description} secondaryActions={<SettingsGearLink />} />
      <div style={{ marginBottom: spacing.lg }}>
        <AgingHeroStrip
          buckets={summaryData.outstanding.buckets}
          totalOutstanding={summaryData.outstanding.total}
          activeBucket={activeBucket}
          onBucketClick={(b) => setBucket(activeBucket === b ? null : b)}
        />
      </div>

      {activeBucket && (
        <div style={{ marginBottom: spacing.md }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '5px 6px 5px 12px',
              background: palette.accentWash,
              border: `1px solid ${palette.accent}`,
              borderRadius: radii.full,
              fontSize: typography.size.caption,
              fontWeight: typography.weight.medium,
              color: palette.accent,
            }}
          >
            {`Filtered · ${BUCKET_LABEL[activeBucket]}`}
            <button
              type="button"
              onClick={() => setBucket(null)}
              aria-label="Clear aging filter"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 18,
                height: 18,
                border: 'none',
                borderRadius: radii.full,
                background: palette.accent,
                color: palette.white,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              <X size={12} strokeWidth={2.5} />
            </button>
          </span>
        </div>
      )}

      <div
        style={{
          background: palette.white,
          borderRadius: admin.cardRadius,
          border: `1px solid ${admin.border}`,
          overflow: 'hidden',
          boxShadow: shadow.card,
        }}
      >
        {visibleRows.length === 0 ? (
          <div style={{ padding: spacing.xl, textAlign: 'center', fontSize: typography.size.caption, color: admin.caption }}>
            No invoices in the {BUCKET_LABEL[activeBucket as AgingBucket].toLowerCase()} bucket.
          </div>
        ) : (
          <OutstandingTable
            rows={visibleRows}
            onRowTap={handleTap}
            onRowRemind={handleRemind}
          />
        )}
      </div>
      {toast && (
        <Toast
          message={toast.msg}
          type={toast.type}
          isVisible={!!toast}
          onDismiss={() => setToast(null)}
        />
      )}
      {detailPanel}
    </div>
  );
}
